<template>
  <div
    class="divide-y group flex flex-col"
    :class="[dark ? 'bg-gray-900 divide-gray-700' : 'bg-gray-200 divide-gray-300']"
  >
    <div class="flex flex-1 min-h-24" v-for="{ shape, stage } in shapes" :key="stage.id">
      <div v-if="!compact" class="flex-1 py-4">
        <h3 class="text-lg font-medium">
          {{ stage.name }} ({{ getFormattedAverage(stage.average_audience_count) }})
        </h3>
        <ul class="text-gray-600 text-sm">
          <li v-for="audience in stage.audiences" :key="audience.id">
            {{ audience.name }}
          </li>
        </ul>
      </div>
      <div class="flex-1 relative">
        <div class="w-full aspect-w-16 aspect-h-5" />
        <svg
          viewBox="0 0 100 100"
          class="fill-current w-full h-full absolute top-0 left-0"
          :style="{ color: shape.backgroundColor }"
          preserveAspectRatio="none"
        >
          <polygon :points="shape.path" />
        </svg>
        <div
          v-if="compact"
          class="opacity-0 cursor-default group-hover:opacity-100 transition-opacity absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white bg-opacity-75 text-center px-3 py-2 text-xs rounded-md max-w-full"
        >
          <p class="text-center mb-2 truncate">
            {{ stage.name }}
          </p>
          <p class="truncate">
            {{ getFormattedAverage(stage.average_audience_count) }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Numbro from '@/utils/numbro';

const colors = [
  '#3b82f6', // blue
  '#6366f1', // Indigo
  '#8b5cf6', // Violet
  '#a855f7', // Purple
  '#d946ef', // Fuchsia
  '#ec4899', // Pink
  '#f43f5e', // Rose
  '#ef4444', // Red
  '#f97316', // Orange
  '#f59e0b', // Amber
  '#eab308', // Yellow
];

export default {
  props: {
    stages: {
      type: Array,
      default: () => [],
    },
    dark: Boolean,
    compact: Boolean,
  },
  computed: {
    maxAverageAudienceCount() {
      return (this.stages || []).reduce((max, current) => {
        const count = current?.average_audience_count || 0;
        return count > max ? count : max;
      }, 0);
    },
    trapezoidBasesByShapeId() {
      return this.stages.reduce(
        (carry, current, currentIndex) => ({
          ...carry,
          [current.id]: this.calculateTrapezoidForStage(current, currentIndex),
        }),
        {}
      );
    },
    totalStages() {
      return this.stages.length;
    },
    shapes() {
      const max = this.maxAverageAudienceCount;
      const funnelSmallestOpening = (100 - 15) / 2;

      if (!max) {
        return [];
      }

      return this.stages.reduce((shapes, curStage, curStageIndex, stages) => {
        const nextStageIndex = curStageIndex + 1;
        const nextStage = nextStageIndex < stages.length ? stages[nextStageIndex] : curStage;

        const topBase = curStage.average_audience_count
          ? (curStage.average_audience_count / max) * funnelSmallestOpening
          : 0;
        const bottomBase = nextStage.average_audience_count
          ? (nextStage.average_audience_count / max) * funnelSmallestOpening
          : 0;

        const trapezoidPoints = [
          `${funnelSmallestOpening - topBase},0`,
          `${100 - funnelSmallestOpening + topBase},0`,
          `${100 - funnelSmallestOpening + bottomBase},100`,
          `${funnelSmallestOpening - bottomBase},100`,
        ];

        const colorIndex = Math.round((curStageIndex / (stages.length - 1)) * (colors.length - 1));

        return [
          ...shapes,
          {
            shape: {
              path: trapezoidPoints.join(' '),
              backgroundColor: colors[colorIndex],
            },
            stage: curStage,
          },
        ];
      }, []);
    },
  },
  methods: {
    calculateTrapezoidForStage(stage, index) {
      const nextStage = this.stages[index + 1] ? this.stages[index + 1] : stage;

      const top = this.getStageColumnPercentWidth(stage);
      const bottom = this.getStageColumnPercentWidth(nextStage);
      const colorIndex = Math.round((index / (this.totalStages - 1)) * (colors.length - 1));

      return {
        top,
        bottom,
        longest: top > bottom ? top : bottom,
        shortest: top > bottom ? bottom : top,
        longestTop: top > bottom,
        equalBases: top === bottom,
        colorIndex,
        backgroundColor: colors[colorIndex],
      };
    },
    getStageColumnPercentWidth(stage) {
      if (!stage.average_audience_count || !this.maxAverageAudienceCount) {
        return 0;
      }

      const initialWidth = Math.floor(
        (stage.average_audience_count / this.maxAverageAudienceCount) * 100
      );
      const width = initialWidth - 10;

      return width < 10 ? 10 : width;

      // return Math.floor((stage.average_audience_count / this.maxAverageAudienceCount) * 100);
    },
    getPercentMargin(shape) {
      const initialPercentMargin = (100 - shape.longest) / 2;
      const percentMargin = initialPercentMargin - 5;

      return `${percentMargin < 0 ? 0 : percentMargin}%`;
    },
    getFormattedAverage(num) {
      return Numbro(num || 0).format({ thousandSeparated: true });
    },
  },
};
</script>
